import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Grid,
  CardContent,
  CardActions,
  Chip,
  Stack,
  alpha,
  useTheme as useMuiTheme,
  Tabs,
  Tab,
  Pagination,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useRoom } from "../contexts/RoomContext";
import { Room, RoomStatus } from "../types";
import Loading from "../components/common/Loading";
import ErrorMessage from "../components/common/ErrorMessage";
import StyledCard from "../components/common/StyledCard";
import StyledButton from "../components/common/StyledButton";

/**
 * Dashboard Component
 */
const Dashboard = () => {
  const { rooms, loading, error, fetchUserRooms } = useRoom();
  const navigate = useNavigate();
  const theme = useMuiTheme();
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    fetchUserRooms();
  }, [fetchUserRooms]);

  useEffect(() => {
    // Reset pagination when tab changes
    setPage(1);
  }, [tabValue]);

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const handleJoinRoom = (roomId: string) => {
    navigate(`/rooms/${roomId}`);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  /**
   * Room Card Component
   */
  const RoomCard = ({ room }: { room: Room }) => {
    const statusColors = {
      [RoomStatus.SCHEDULED]: {
        bg: theme.palette.info.main,
        text: theme.palette.info.contrastText,
      },
      [RoomStatus.LIVE]: {
        bg: theme.palette.success.main,
        text: theme.palette.success.contrastText,
      },
      [RoomStatus.CLOSED]: {
        bg:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.grey[500], 0.2)
            : alpha(theme.palette.grey[500], 0.1),
        text: theme.palette.text.secondary,
      },
    };

    const statusColor =
      statusColors[room.status] || statusColors[RoomStatus.CLOSED];

    return (
      <StyledCard
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "visible",
          "&::before":
            room.status === RoomStatus.LIVE
              ? {
                content: '""',
                position: "absolute",
                top: -6,
                right: -6,
                width: 16,
                height: 16,
                background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`,
                borderRadius: "50%",
                boxShadow: `0 0 0 4px ${alpha(
                  theme.palette.success.main,
                  0.3
                )}`,
                animation: "pulse 1.5s infinite",
                zIndex: 1,
                "@keyframes pulse": {
                  "0%": {
                    boxShadow: `0 0 0 0 ${alpha(
                      theme.palette.success.main,
                      0.6
                    )}`,
                  },
                  "70%": {
                    boxShadow: `0 0 0 8px ${alpha(
                      theme.palette.success.main,
                      0
                    )}`,
                  },
                  "100%": {
                    boxShadow: `0 0 0 0 ${alpha(
                      theme.palette.success.main,
                      0
                    )}`,
                  },
                },
              }
              : {},
        }}
      >
        {/* Card header with title and status */}
        <CardContent sx={{ p: 0, flexGrow: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTopLeftRadius: "inherit",
              borderTopRightRadius: "inherit",
              p: 2.5,
              borderBottom: `1px solid ${alpha(
                theme.palette.divider,
                0.5
              )}`,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.05
              )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              fontWeight="700"
              noWrap
              sx={{ maxWidth: "70%" }}
            >
              {room.title}
            </Typography>
            <Chip
              label={room.status.toUpperCase()}
              size="small"
              sx={{
                background: `linear-gradient(135deg, ${statusColor.bg
                  } 0%, ${alpha(statusColor.bg, 0.8)} 100%)`,
                color: statusColor.text,
                fontWeight: 700,
                fontSize: "0.7rem",
                px: 1,
                boxShadow: `0 2px 8px ${alpha(
                  statusColor.bg,
                  0.3
                )}`,
              }}
            />
          </Box>

          {/* Card content with room details */}
          <Box sx={{ p: 2.5 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{
                minHeight: "40px",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                mb: 2,
                lineHeight: 1.6,
              }}
            >
              {room.description}
            </Typography>

            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>Start</span>
                <span style={{ color: theme.palette.text.secondary }}>{formatDate(room.startTime)}</span>
              </Typography>
              <Typography
                variant="body2"
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>End</span>
                <span style={{ color: theme.palette.text.secondary }}>{formatDate(room.endTime)}</span>
              </Typography>
              <Typography
                variant="body2"
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <span style={{ fontWeight: 600, color: theme.palette.text.primary }}>Code</span>
                <Chip
                  label={room.code}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 600,
                    height: 24,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.05)
                  }}
                />
              </Typography>
            </Stack>

            {/* Tags */}
            {room.tags.length > 0 && (
              <Box mt={2.5} display="flex" flexWrap="wrap" gap={0.5}>
                {room.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      color: theme.palette.secondary.main,
                      fontSize: "0.7rem",
                      height: 24,
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </CardContent>

        {/* Card actions */}
        <CardActions sx={{ p: 2.5, pt: 0 }}>
          <StyledButton
            size="medium"
            color="primary"
            variant={room.status === RoomStatus.LIVE ? "contained" : "outlined"}
            onClick={() => handleJoinRoom(room._id)}
            fullWidth
            className="dashboard-join-btn"
            sx={{
              borderRadius: 3,
              py: 1.2,
              fontWeight: 600,
            }}
          >
            {room.status === RoomStatus.LIVE ? "Join Now" : "View Details"}
          </StyledButton>
        </CardActions>
      </StyledCard>
    );
  };

  /**
   * Empty Message Component
   */
  const EmptyMessage = ({ message }: { message: string }) => (
    <Box py={8} display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%">
      <Typography variant="h6" color="text.secondary" textAlign="center" gutterBottom>
        {message}
      </Typography>
      <StyledButton
        variant="contained"
        color="primary"
        onClick={() => navigate('/create-room')}
        sx={{ mt: 2 }}
      >
        Create a Room
      </StyledButton>
    </Box>
  );

  if (loading) return <Loading message="Loading your rooms..." />;

  // Filter rooms based on the selected tab
  const getFilteredRooms = () => {
    switch (tabValue) {
      case 0: // All rooms
        return [
          ...rooms.live,
          ...rooms.upcoming,
          ...rooms.past,
          ...rooms.invites,
        ];
      case 1: // Live rooms
        return rooms.live;
      case 2: // Upcoming rooms
        return rooms.upcoming;
      case 3: // Past rooms
        return rooms.past;
      case 4: // Invitations
        return rooms.invites;
      default:
        return [];
    }
  };

  const filteredRooms = getFilteredRooms();

  // Calculate pagination
  const totalPages = Math.ceil(filteredRooms.length / itemsPerPage);
  const displayedRooms = filteredRooms.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  return (
    <Box
      sx={{
        mb: 4,
        minHeight: "100vh",
        width: "100%",
        padding: 0,
        bgcolor: "background.default",
        backgroundImage:
          theme.palette.mode === "light"
            ? `radial-gradient(circle at 20% 80%, ${alpha(
              theme.palette.primary.main,
              0.03
            )} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${alpha(
              theme.palette.secondary.main,
              0.03
            )} 0%, transparent 50%)`
            : `radial-gradient(circle at 20% 80%, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${alpha(
              theme.palette.secondary.main,
              0.05
            )} 0%, transparent 50%)`,
        py: 4,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Dashboard Header */}
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          mb: 4,
          animation: "slideInTop 0.6s ease-out",
          "@keyframes slideInTop": {
            from: {
              opacity: 0,
              transform: "translateY(-20px)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
        id="dashboard-header"
      >
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 800,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textAlign: "center",
            mb: 1.5,
            fontSize: { xs: "2rem", md: "3rem" },
            letterSpacing: "-0.02em",
          }}
        >
          Your Dashboard
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          paragraph
          sx={{
            textAlign: "center",
            fontSize: { xs: "1rem", md: "1.1rem" },
            maxWidth: "600px",
            mx: "auto",
            lineHeight: 1.6,
          }}
        >
          Manage your upcoming, live, and past events. Join, create, and explore virtual spaces.
        </Typography>
      </Box>

      <ErrorMessage message={error} />

      {/* Room Filter Tabs */}
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          mb: 4,
          animation: "slideInTop 0.7s ease-out",
          "@keyframes slideInTop": {
            from: {
              opacity: 0,
              transform: "translateY(-20px)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0)",
            },
          },
        }}
        id="dashboard-tabs"
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: alpha(theme.palette.divider, 0.1),
            background: alpha(theme.palette.background.paper, 0.5),
            borderRadius: 4,
            p: 1,
            backdropFilter: "blur(10px)",
            maxWidth: "800px",
            mx: "auto",
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            centered={true}
            sx={{
              "& .MuiTab-root": {
                fontWeight: 600,
                transition: "all 0.3s ease",
                borderRadius: 3,
                mx: 0.5,
                minHeight: 48,
                "&.Mui-selected": {
                  color: theme.palette.primary.main,
                  background: alpha(theme.palette.primary.main, 0.1),
                },
                "&:hover": {
                  background: alpha(theme.palette.primary.main, 0.05),
                },
              },
              "& .MuiTabs-indicator": {
                height: 0,
              },
            }}
          >
            <Tab label="All Rooms" id="tab-all" />
            <Tab
              id="tab-live"
              label={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {rooms.live.length > 0 && (
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: "success.main",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        display: "inline-block",
                        mr: 1,
                      }}
                    />
                  )}
                  Live Now
                </Box>
              }
              disabled={rooms.live.length === 0}
            />
            <Tab label="Upcoming" id="tab-upcoming" />
            <Tab label="Past Events" id="tab-past" />
            <Tab label="Invites" id="tab-invites"
              disabled={rooms.invites.length === 0}
            />
          </Tabs>
        </Box>
      </Box>

      {/* Room Grid */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, animation: "slideInTop 0.8s ease-out", "@keyframes slideInTop": { from: { opacity: 0, transform: "translateY(-20px)" }, to: { opacity: 1, transform: "translateY(0)" } } }} id="dashboard-room-grid">
        {displayedRooms.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {displayedRooms.map((room, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={`room-${room._id}-${index}`}
                >
                  <RoomCard room={room} />
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                mt={6}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  size="large"
                />
              </Box>
            )}
          </>
        ) : (
          <EmptyMessage
            message={`No ${tabValue === 0
              ? ""
              : tabValue === 1
                ? "live"
                : tabValue === 2
                  ? "upcoming"
                  : tabValue === 3
                    ? "past"
                    : "invitation"
              } rooms found`}
          />
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;
